import { api } from './index';

export interface FeeStatisticsRow {
  id: string;
  label: string;
  level: number;
  parentId?: string;
  receivableBeforeTax: number;
  taxAndSurcharge: number;
  exemption: number;
  totalReceivable: number;
  collected: number;
  debt: number;
  children?: FeeStatisticsRow[];
}

export interface FeeDetailRow {
  id: string;
  apartmentId: string;
  apartmentCode: string;
  billingPeriod: string;
  feeCategoryId: string;
  feeCategoryName: string;
  openingDebt: number;
  incurredAmount: number;
  surchargeAmount: number;
  taxAmount: number;
  totalReceivable: number;
  paidAmount: number;
  closingDebt: number;
  status: string;
}

export interface BulkCreationRecord {
  id: string;
  buildingId: string;
  billingPeriod: string;
  feeCategoryIds: string[];
  feeCategoryNames?: string[];
  apartmentGroupIds?: string[];
  apartmentIds?: string[];
  groupLabel?: string;
  apartmentLabel?: string;
  feesCreatedCount?: number;
  overwrite: boolean;
  createdCount: number;
  updatedCount: number;
  skippedCount: number;
  errorCount: number;
  details?:
    | Array<{ apartmentCode: string; feeCategory: string; action: string; message?: string }>
    | {
        rows?: Array<{ apartmentCode: string; feeCategory: string; action: string; message?: string }>;
        warnings?: string[];
        parkingAudits?: unknown[];
      };
  createdAt: string;
  creator?: { name: string; email: string };
  revertedAt?: string | null;
  revertedBy?: string | null;
  revertDeletedCount?: number | null;
  revertSkippedPaidCount?: number | null;
  reverter?: { name: string; email: string };
  building?: { name: string };
  summary?: {
    createdCount: number;
    updatedCount: number;
    purgedCount?: number;
    skippedCount: number;
    errorCount: number;
  };
}

export interface FeeDetailsImportPreviewItem {
  apartmentCode: string;
  feeCategoryName: string;
  billingPeriod: string;
  status: 'ready' | 'will_create' | 'unchanged' | 'skipped';
  reason?: string;
  surplusResync?: boolean;
  openingDebtBefore: number;
  openingDebtAfter: number;
  paidAmountBefore: number;
  paidAmountAfter: number;
  closingDebtBefore: number;
  closingDebtAfter: number;
  surplusBalanceAfter?: number;
}

export interface FeeDetailsImportPreviewResult {
  format: 'legacy-handover' | 'long';
  formatLabel: string;
  totalRows: number;
  readyCount: number;
  willCreateCount: number;
  unchangedCount: number;
  surplusResyncCount: number;
  skippedCount: number;
  apartmentCount: number;
  summary: {
    openingDebtBefore: number;
    openingDebtAfter: number;
    paidAmountBefore: number;
    paidAmountAfter: number;
    surplusBalance: number;
  };
  items: FeeDetailsImportPreviewItem[];
  errors: string[];
}

export interface ServiceFeeImportBatch {
  id: string;
  buildingId: string;
  billingPeriod: string;
  fileName: string;
  fileUrl?: string | null;
  totalRows: number;
  createdCount: number;
  updatedCount: number;
  skippedCount: number;
  errorCount: number;
  errors?: string[] | null;
  createdAt: string;
  createdById?: string | null;
  creator?: { id: string; name: string; email?: string | null } | null;
  revertedAt?: string | null;
  revertedById?: string | null;
  revertDeletedCount?: number | null;
  revertRestoredCount?: number | null;
  revertSkippedPaidCount?: number | null;
  reverter?: { id: string; name: string; email?: string | null } | null;
}

export const serviceFeeManagementApi = {
  getStatistics: async (buildingId: string, billingPeriod: string): Promise<FeeStatisticsRow[]> => {
    const response = await api.get('/service-fee-management/statistics', {
      params: { buildingId, billingPeriod },
    });
    return response.data;
  },

  getDetails: async (params: {
    buildingId: string;
    billingPeriod: string;
    feeCategoryId?: string;
    search?: string;
    status?: string;
  }): Promise<{
    categories: Array<{ id: string; name: string; feeCode: string | null }>;
    items: FeeDetailRow[];
    total: number;
  }> => {
    const response = await api.get('/service-fee-management/details', { params });
    return response.data;
  },

  bulkCreate: async (data: {
    buildingId: string;
    billingPeriod: string;
    feeCategoryIds: string[];
    overwrite?: boolean;
    includeZeroAmount?: boolean;
    apartmentGroupIds?: string[];
    apartmentIds?: string[];
  }): Promise<BulkCreationRecord> => {
    const response = await api.post('/service-fee-management/bulk-create', data);
    return response.data;
  },

  getBulkCreateHistory: async (
    buildingId: string,
    page = 1,
    limit = 20,
    billingPeriod?: string,
  ): Promise<{ items: BulkCreationRecord[]; total: number; page: number; limit: number }> => {
    const response = await api.get('/service-fee-management/bulk-create-history', {
      params: { buildingId, page, limit, ...(billingPeriod ? { billingPeriod } : {}) },
    });
    return response.data;
  },

  revertBulkCreation: async (
    historyId: string,
  ): Promise<{ deletedCount: number; skippedPaidCount: number }> => {
    const response = await api.delete(`/service-fee-management/bulk-create-history/${historyId}`);
    return response.data;
  },

  previewFeeDetailsImport: async (
    buildingId: string,
    billingPeriod: string,
    file: File,
  ): Promise<FeeDetailsImportPreviewResult> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('buildingId', buildingId);
    formData.append('billingPeriod', billingPeriod);
    const response = await api.post('/service-fee-management/details/import-preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  importFeeDetails: async (
    buildingId: string,
    billingPeriod: string,
    file: File,
  ): Promise<{
    updatedCount: number;
    createdCount: number;
    surplusSyncedCount?: number;
    skippedCount: number;
    totalRows: number;
    errors: string[];
  }> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('buildingId', buildingId);
    formData.append('billingPeriod', billingPeriod);
    const response = await api.post('/service-fee-management/details/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  downloadFeeDetailsSample: async (
    buildingId: string,
    billingPeriod: string,
    feeCategoryId?: string,
  ): Promise<void> => {
    const response = await api.get('/service-fee-management/details/export-sample', {
      params: {
        buildingId,
        billingPeriod,
        ...(feeCategoryId ? { feeCategoryId } : {}),
      },
      responseType: 'blob',
    });
    const disposition = response.headers['content-disposition'] as string | undefined;
    const utf8FilenameMatch = disposition?.match(/filename\*=UTF-8''([^;]+)/i);
    const asciiFilenameMatch = disposition?.match(/filename="([^"]+)"/);
    const filename = utf8FilenameMatch?.[1]
      ? decodeURIComponent(utf8FilenameMatch[1])
      : asciiFilenameMatch?.[1] || `mau-nhap-chi-tiet-phi-${billingPeriod.replace('/', '-')}.xlsx`;
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },

  getFeeDetailsImportHistory: async (
    buildingId: string,
    billingPeriod?: string,
  ): Promise<ServiceFeeImportBatch[]> => {
    const response = await api.get('/service-fee-management/details/import-history', {
      params: { buildingId, ...(billingPeriod ? { billingPeriod } : {}) },
    });
    return response.data;
  },

  revertFeeDetailsImport: async (
    batchId: string,
  ): Promise<{
    deletedCount: number;
    restoredCount: number;
    skippedPaidCount: number;
  }> => {
    const response = await api.delete(`/service-fee-management/details/import-history/${batchId}`);
    return response.data;
  },
};
